import React, { useState } from "react";

import { useMutation } from "@apollo/client";
import {
  EventNote as EventNoteIcon,
  FileCopyOutlined as FileCopyOutlinedIcon,
  RadioButtonChecked as RadioButtonCheckedIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Subject as SubjectIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";
import {
  addMinutes,
  hoursToMinutes,
  roundToNearestMinutes,
  startOfDay,
} from "date-fns";
import { Controller, FormProvider } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import * as yup from "yup";

import { Features } from "@/config/features";
import { graphql } from "@/gql";
import { SendFormNowInput, ShareFormInput } from "@/gql/graphql";
import useFormWithYup from "@/hooks/useFormWithYup";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { maybeCall } from "@/pages/Clients/ClientDetails/ClientDiary/Meals/utils/maybeCall";
import { sentFormsQuery } from "@/pages/Forms/SentForms/SentForms";
import { colors } from "@/theme/overrides/colors";
import { formatISODate, formatISOTimeOfDay } from "@/utils/datetime";
import { muiCheckbox, muiField } from "@/utils/hookForm";

import ClientListAutoComplete from "./ClientListAutoComplete";
import DatePicker from "./DatePicker";
import RepeatPicker from "./RepeatPicker";
import TimePicker from "./TimePicker";

interface Props {
  formId?: string;
  onClose?: () => void;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: 20,
    backgroundColor: colors.white,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
    paddingBottom: 24,
    borderBottom: "1px solid #DEE6F0",
  },
  share: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    marginBottom: 24,
  },
  shareLabel: {
    marginBottom: 12,
  },
  shareClients: {
    display: "flex",
    flexWrap: "wrap",
    margin: -2,
    marginTop: theme.spacing(4),
  },
  schedule: {
    padding: 20,
    paddingTop: 12,
    border: "1px solid #DEE6F0",
  },
  scheduleStart: {
    marginLeft: 20,
  },
  scheduleHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginRight: -12,
  },
  scheduleLabel: {
    display: "flex",
    alignItems: "center",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 24,
    borderTop: "1px solid #DEE6F0",
  },
  actions: {
    "& > :not(:last-child)": {
      marginRight: 10,
    },
  },
  scheduleOutline: {
    marginLeft: 16,
    border: 0.5,
    borderStyle: "solid",
    borderColor: "#D4DBE5",
    padding: 11,
  },
}));

function getNextFifteenMinutes() {
  const nextQuarter = roundToNearestMinutes(new Date(), {
    nearestTo: 15,
    roundingMethod: "ceil",
  });
  return hoursToMinutes(nextQuarter.getHours()) + nextQuarter.getMinutes();
}
const scheduledFormSchema = yup
  .object({
    client: yup
      .object({
        id: yup.string().required(),
        email: yup.string().optional().nullable(),
        profile: yup
          .object({
            id: yup.string().required(),
            fullName: yup.string().required(),
            avatarUrl: yup.string().required(),
          })
          .optional()
          .nullable(),
      })
      .nullable()
      .optional()
      .default(null),
    email: yup.string().optional(),
    sendsOn: yup.date().required().default(new Date()),
    sendsAt: yup.number().required().default(getNextFifteenMinutes()), // number of minutes since midnight
    sendsAtTz: yup
      .string()
      .required()
      .default(Intl.DateTimeFormat().resolvedOptions().timeZone),
    recurrence: yup.string().presence().nullable(),
    message: yup.string().nullable(),
  })
  .required();

type ScheduleFormValues = ReturnType<typeof scheduledFormSchema.cast>;

const validationSchema = yup
  .object({
    publishedToCommunity: yup.boolean().required(),
    scheduledFormsAttributes: yup.array(scheduledFormSchema).defined(),
    isSameScheduleToAll: yup.boolean().required(),
    scheduleForAll: scheduledFormSchema,
  })
  .required();

export type FormValues = ReturnType<typeof validationSchema.cast>;

export function defaultSchedule(): ScheduleFormValues {
  return {
    sendsOn: new Date(),
    sendsAt: getNextFifteenMinutes(),
    // TODO: use current user's saved timezone settings instead of machine settings
    sendsAtTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    recurrence: "",
  } as ScheduleFormValues;
}

const shareFormMutation = graphql(`
  mutation ShareForm($input: ShareFormInput!) {
    shareForm(input: $input) {
      form {
        id
      }
    }
  }
`);

const generateFormLinkMutation = graphql(`
  mutation GenerateFormLink($id: ID!) {
    generateFormLink(input: { id: $id }) {
      url
    }
  }
`);

const sendFormNowMutation = graphql(`
  mutation SendFormNow($input: SendFormNowInput!) {
    sendFormNow(input: $input) {
      form {
        id
      }
    }
  }
`);

const ShareFormPanel: React.FC<Props> = ({ formId, onClose }) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();
  const [sendMethod, setSendMethod] = useState<"now" | "later">("now");

  const formMethods = useFormWithYup<FormValues>({
    defaultValues: {
      publishedToCommunity: false,
      scheduledFormsAttributes: [],
      isSameScheduleToAll: true,
      scheduleForAll: defaultSchedule(),
    },
    validationSchema,
  });
  const { control, watch, handleSubmit, setValue, reset } = formMethods;
  const [shareForm] = useMutation(shareFormMutation);
  const [sendFormNow] = useMutation(sendFormNowMutation);
  const [generateFormLink] = useMutation(generateFormLinkMutation);

  async function submit(values: FormValues) {
    if (formId == null) return;
    if (sendMethod == "later") {
      const input: ShareFormInput = {
        id: formId,
        publishedToCommunity: values.publishedToCommunity,
        scheduledFormsAttributes: values.scheduledFormsAttributes.map(
          (attr) => {
            const sched = values.isSameScheduleToAll
              ? values.scheduleForAll
              : attr;

            return {
              clientId: attr.client?.id,
              email: attr.email,
              sendsOn: formatISODate(sched.sendsOn),
              sendsAt: formatISOTimeOfDay(
                addMinutes(startOfDay(new Date()), sched.sendsAt)
              ),
              sendsAtTz: sched.sendsAtTz,
              recurrence: sched.recurrence,
              message: sched.message,
            };
          }
        ),
      };
      await shareForm({ variables: { input } });
      maybeCall(onClose);
      snackbar.open("Form sent successfully");
    } else {
      const input: SendFormNowInput = {
        id: formId,
        sentFormsAttributes: values.scheduledFormsAttributes.map((attr) => {
          return {
            clientId: attr.client?.id,
          };
        }),
      };
      try {
        await sendFormNow({
          variables: { input },
          refetchQueries: [sentFormsQuery],
        });
        maybeCall(onClose);
        snackbar.open("Form sent successfully");
      } catch (error) {
        snackbar.open(`Unable to send form ${error}`);
      }
    }
    reset();
  }

  async function copyFormLink() {
    if (formId == null) return;

    const { data } = await generateFormLink({ variables: { id: formId } });
    if (data?.generateFormLink?.url) {
      await navigator.clipboard.writeText(data?.generateFormLink?.url);
      snackbar.open("Copied link to clipboard");
    }
  }
  const handleMethodChange = (event: SelectChangeEvent<"now" | "later">) => {
    setSendMethod(event.target.value as typeof sendMethod);
  };
  const handleRemove = (index: number) => {
    const currentValue = watch("scheduledFormsAttributes");

    const updatedValue = currentValue.filter((_, i) => i !== index);

    setValue("scheduledFormsAttributes", updatedValue);
  };
  return (
    <div className={classes.root}>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(submit)}>
          <div className={classes.header}>
            <Typography variant="h5">Send Form</Typography>
            {Features.formCopyLinkEnabled && (
              <Button
                variant="outlined"
                startIcon={<FileCopyOutlinedIcon />}
                onClick={copyFormLink}
              >
                Copy Link
              </Button>
            )}
          </div>
          <div className={classes.share}>
            <InputLabel className={classes.shareLabel}>Send to:</InputLabel>
            <Controller
              control={control}
              name="scheduledFormsAttributes"
              render={({ field }) => (
                <ClientListAutoComplete
                  value={field.value}
                  onChange={field.onChange as any}
                />
              )}
            />
            <div className={classes.shareClients}>
              {watch("scheduledFormsAttributes").map((scheduledForm, index) => (
                <div key={index} style={{ display: "inline", padding: 2 }}>
                  <Chip
                    avatar={
                      <Avatar
                        alt=""
                        src={
                          scheduledForm.client?.profile?.avatarUrl ?? undefined
                        }
                      />
                    }
                    label={
                      scheduledForm.client?.profile?.fullName ??
                      scheduledForm.email
                    }
                    onDelete={() => handleRemove(index)}
                  />
                </div>
              ))}
            </div>
          </div>
          {watch("isSameScheduleToAll") &&
            watch("scheduledFormsAttributes").length > 0 && (
              <div className={classes.schedule}>
                <div className={classes.scheduleHeader}>
                  <Typography variant="h6">
                    Send to{" "}
                    {watch("scheduledFormsAttributes").length === 1
                      ? watch("scheduledFormsAttributes")[0].client?.profile
                          ?.fullName ??
                        watch("scheduledFormsAttributes")[0].email
                      : "all recipients"}
                  </Typography>
                  <Button
                    variant="text"
                    onClick={() =>
                      setValue("scheduleForAll", defaultSchedule())
                    }
                  >
                    Clear
                  </Button>
                </div>
                <Grid container spacing={4}>
                  <Grid container item xs={12}>
                    <Grid item lg={1} xs={2} className={classes.scheduleLabel}>
                      <EventNoteIcon />
                    </Grid>
                    <Grid item xs={10} className={classes.scheduleLabel}>
                      <Select
                        value={sendMethod}
                        color="primary"
                        onChange={handleMethodChange}
                        variant="outlined"
                      >
                        <MenuItem value="now">Send now</MenuItem>
                        <MenuItem value="later">Send later</MenuItem>
                      </Select>
                      {sendMethod === "later" && (
                        <div className={classes.scheduleOutline}>
                          <Controller
                            control={control}
                            name="scheduleForAll.sendsOn"
                            render={({ field }) => (
                              <DatePicker
                                value={field.value}
                                onChange={(date) => {
                                  if (date) {
                                    field.onChange(date);
                                  }
                                }}
                              />
                            )}
                          />
                          <Controller
                            control={control}
                            name="scheduleForAll.sendsAt"
                            render={({ field }) => (
                              <TimePicker
                                {...field}
                                disablePast
                                sendsOn={watch(
                                  "scheduleForAll.sendsOn" as const
                                )}
                              />
                            )}
                          />
                        </div>
                      )}
                    </Grid>
                  </Grid>
                  {sendMethod === "later" && (
                    <Grid container item xs={12}>
                      <Grid
                        item
                        lg={1}
                        xs={2}
                        className={classes.scheduleLabel}
                      >
                        <Typography>Repeat</Typography>
                      </Grid>
                      <Grid item md={5} xs={10}>
                        <Controller
                          control={control}
                          name="scheduleForAll.recurrence"
                          render={({ field }) => (
                            <RepeatPicker
                              {...muiField(field)}
                              defaultValue=""
                              sendsOn={watch("scheduleForAll.sendsOn")}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  )}
                  <Grid container item xs={12}>
                    <Grid item lg={1} xs={2} className={classes.scheduleLabel}>
                      <SubjectIcon />
                    </Grid>
                    <Grid item md={5} xs={10}>
                      <Controller
                        control={control}
                        name="scheduleForAll.message"
                        render={({ field }) => (
                          <TextField
                            {...muiField(field)}
                            placeholder="Enter message (optional)"
                            multiline
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </div>
            )}
          <div className={classes.footer}>
            <Controller
              control={control}
              name="publishedToCommunity"
              render={({ field }) => (
                <FormControlLabel
                  {...muiCheckbox(field)}
                  control={
                    <Checkbox
                      color="primary"
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<RadioButtonCheckedIcon />}
                    />
                  }
                  label="Publish to community"
                />
              )}
            />
            <div className={classes.actions}>
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">Send</Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default ShareFormPanel;
